import React, { Component } from "react";
import { observer } from "mobx-react";

import { defined } from "@formant/core/common/defined";
import { IAnnotationTemplate } from "@formant/core/model/IAnnotationTemplate";

import { annotationTemplateStore, routerStore } from "../../../stores";
import { localSettingObservable } from "../../../utils/localSettingObservable";
import {
    DataTable,
    defaultSimpleSortState,
    ISimpleSortState,
} from "../../common/DataTable";
import { IDataTableColumn } from "../../common/DataTable/IDataTableColumn";
import { Help } from "../../common/Help";
import { Scrollable } from "../../common/Scrollable";
import {
    applySimpleFilter,
    emptySimpleFilterState,
    ISimpleFilterState,
    SimpleFilter,
    tagDimensions,
} from "../../common/SimpleFilter";
import { StandardButton } from "../../common/StandardButton";
import { StatsHeader } from "../../common/StatsHeader";
import { Tags } from "../../common/Tags";
import { RowDate } from "../RowDate";
import { RowTitle } from "../RowTitle";
import { SettingsPage } from "../SettingsPage";
import { Actions } from "./Actions";

@observer
export class AnnotationTemplates extends Component {
    @localSettingObservable("settings-annotations-filter")
    private filter: ISimpleFilterState = emptySimpleFilterState;

    @localSettingObservable("settings-annotations-sort")
    private sort: ISimpleSortState = defaultSimpleSortState;

    public render() {
        const { routeTo } = routerStore;
        const columns: IDataTableColumn<IAnnotationTemplate>[] = [
            {
                header: "Name",
                row: _ => <RowTitle title={_.name} icon="annotation" />,
                sortBy: _ => _.name,
            },
            {
                header: "Tags",
                row: _ => <Tags tags={_.tags} />,
            },
            {
                header: "Created",
                row: _ => <RowDate date={_.createdAt} />,
                sortBy: _ => _.createdAt,
                hideBelow: "lg",
            },
            {
                header: "Updated",
                row: _ => <RowDate date={_.updatedAt} />,
                sortBy: _ => _.updatedAt,
                hideBelow: "lg",
            },
            {
                header: "",
                row: _ => <Actions annotationTemplate={_} />,
            },
        ];

        const annotationTemplates = annotationTemplateStore.list();
        const { filter, onChangeFilter, sort, onChangeSort } = this;
        const dimensions = tagDimensions(
            annotationTemplates || [],
            _ => _.tags,
        );
        const filteredAnnotationTemplates = applySimpleFilter(
            annotationTemplates || [],
            dimensions,
            getAnnotationText,
            filter,
        );

        return (
            <SettingsPage
                title="Annotations"
                extra={
                    <Help
                        text="Annotations relate data in Google Sheets to Formant telemetry data"
                        to="https://docs.formant.io/docs/integrate-with-google-sheets#annotation-definition-can-be-imported-from-google-sheets"
                    />
                }
                actions={
                    <StandardButton
                        data-testid="add-annotation-button"
                        to={routeTo.createAnnotationTemplate()}
                    >
                        Add annotation
                    </StandardButton>
                }
            >
                <StatsHeader
                    icon="annotation"
                    loading={!annotationTemplates}
                    totalQuantity={annotationTemplates?.length || 0}
                    totalLabel="Annotations"
                />
                <SimpleFilter
                    dimensions={dimensions}
                    rows={annotationTemplates || []}
                    filter={filter}
                    onChangeFilter={onChangeFilter}
                />
                <Scrollable>
                    <DataTable
                        rows={filteredAnnotationTemplates || []}
                        columns={columns}
                        loading={annotationTemplates === undefined}
                        noResultsText="No annotations found"
                        onRowClicked={this.onEdit}
                        sort={sort}
                        onChangeSort={onChangeSort}
                    />
                </Scrollable>
            </SettingsPage>
        );
    }

    private onChangeFilter = (filter: ISimpleFilterState) => {
        this.filter = filter;
    };

    private onChangeSort = (sort: ISimpleSortState) => {
        this.sort = sort;
    };

    private onEdit = async (annotationTemplate: IAnnotationTemplate) => {
        const { navigate, routeTo } = routerStore;
        await navigate(
            routeTo.editAnnotationTemplate(
                { id: defined(annotationTemplate.id) },
                { tab: "Settings" },
            ),
        );
    };
}

function getAnnotationText(annotation: IAnnotationTemplate): string {
    return [annotation.name, annotation.description].join(" ");
}
